// src/components/Header.tsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import LoginModal from './LoginModal';

type HeaderProps = {
  currentPage?: 'analysis' | 'mypage' | 'admin';
  onNavigate?: (page: 'analysis' | 'mypage' | 'admin') => void;
  onLogoClick?: () => void;
  isAdmin?: boolean;
};

const Header: React.FC<HeaderProps> = ({ currentPage = 'analysis', onNavigate, onLogoClick, isAdmin = false }) => {
  const { user, loading, enabled, error, loginWithGoogle, sendEmailLink, logout } = useAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setLoginModalOpen(false);
    }
  }, [user]);

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
      return;
    }

    if (onNavigate) {
      onNavigate('analysis');
      return;
    }
    window.location.reload();
  };

  return (
    <header className="flex items-center justify-between gap-4 bg-white px-6 py-4 shadow-none border-none">
      <button onClick={handleLogoClick} className="flex items-center focus:outline-none">
        <img src="/logo_2.png" alt="サイトロゴ" className="h-10 w-auto mr-3" />
      </button>
      <div className="flex min-w-0 items-center gap-3">
        {error && (
          <span className="hidden rounded bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700 md:inline">
            {error}
          </span>
        )}
        {!enabled ? (
          <span className="rounded bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-500">
            Firebase未設定
          </span>
        ) : loading ? (
          <span className="rounded bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-500">
            認証確認中...
          </span>
        ) : user ? (
          <>
            <nav className="hidden items-center gap-1 sm:flex">
              <button
                type="button"
                onClick={() => onNavigate?.('analysis')}
                className={`rounded px-3 py-2 text-sm font-semibold ${
                  currentPage === 'analysis'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                分析
              </button>
              <button
                type="button"
                onClick={() => onNavigate?.('mypage')}
                className={`rounded px-3 py-2 text-sm font-semibold ${
                  currentPage === 'mypage'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                マイページ
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => onNavigate?.('admin')}
                  className={`rounded px-3 py-2 text-sm font-semibold ${
                    currentPage === 'admin'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  管理
                </button>
              )}
            </nav>
            <div className="hidden min-w-0 text-right sm:block">
              <p className="truncate text-sm font-semibold text-gray-800">
                {user.displayName || 'ログイン中'}
              </p>
              {user.email && (
                <p className="truncate text-xs text-gray-500">{user.email}</p>
              )}
            </div>
            {user.photoURL && (
              <img
                src={user.photoURL}
                alt=""
                className="h-9 w-9 rounded-full border border-gray-200"
                referrerPolicy="no-referrer"
              />
            )}
            <button
              type="button"
              onClick={() => onNavigate?.('mypage')}
              className="rounded border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 sm:hidden"
            >
              マイページ
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => onNavigate?.('admin')}
                className="rounded border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 sm:hidden"
              >
                管理
              </button>
            )}
            <button
              type="button"
              onClick={logout}
              className="rounded border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              ログアウト
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setLoginModalOpen(true)}
            className="rounded bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600"
          >
            Googleでログイン
          </button>
        )}
      </div>
      <LoginModal
        open={loginModalOpen}
        loading={loading}
        error={error}
        onClose={() => setLoginModalOpen(false)}
        onLogin={() => {
          void loginWithGoogle();
        }}
        onEmailLinkLogin={sendEmailLink}
      />
    </header>
  );
};

export default Header;
